import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse } from 'query-string';

import {
  applyRedeemableRewards,
  clearRewardsSuccess,
  fetchRedeemableRewards,
  fetchRewardsInfo,
  onChangeRedemptionAmountEvent,
  onRedeemRewardsComponentView,
  signupForRewards
} from 'actions/account/rewards';
import { checkCustomerAuthentication } from 'actions/authentication';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import MartyLink from 'components/common/MartyLink';
import RewardsOptIn from 'components/account/Rewards/RewardsOptIn';
import Breadcrumb from 'components/account/Breadcrumb';
import AccountHeader from 'containers/account/AccountHeader';
import BackNav from 'components/account/BackNav';
import RewardsProgress from 'components/account/Rewards/RewardsProgress';
import RedeemableRewards from 'components/checkout/melody/RedeemableRewards';
import RewardsPrimeBanner from 'components/account/Rewards/RewardsPrimeBanner';
import { FAQ } from 'components/icons';
import { PageLoader } from 'components/Loader';
import css from 'styles/containers/account/rewardsDashboard.scss';
import marketplace from 'cfg/marketplace.json';
import LandingSlot from 'containers/LandingSlot';
import { fetchLandingPageInfo } from 'actions/landing/landingPageInfo';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { toThousandsSeparator, toUSD } from 'helpers/NumberFormats';
import { stripSpecialCharsDashReplace } from 'helpers';

const { account: { rewardsDashboardPageName } } = marketplace;
const items = [{ name: 'Rewards' }];

export class RewardsDashboard extends Component {
  componentDidMount() {
    const { checkCustomerAuthentication } = this.props;
    const returnTo = '/rewards/dashboard';

    checkCustomerAuthentication(returnTo, () => this.fetchData(returnTo));
  }

  fetchData(returnTo) {
    const {
      rewards: { rewardsInfo },
      fetchRewardsInfo,
      fetchRedeemableRewards,
      landingPage: { pageInfo },
      fetchLandingPageInfo
    } = this.props;

    if (!rewardsInfo) {
      fetchRewardsInfo({ returnTo });
    }
    fetchRedeemableRewards();

    if (!pageInfo) {
      fetchLandingPageInfo(rewardsDashboardPageName);
    }
  }

  onLandingComponentClick = e => {
    // Send Analytics component click data via trackLegacyEvent(). The format of this data will change in the future
    // but for now we're sending the landing page parameters the way analytics/siteops wants them, mostly
    // the same as how they're currently formatted in legacy
    const { landingPage, trackLegacyEvent } = this.props;
    const { currentTarget } = e;
    const action = `RewardsDashboard-${landingPage.pageInfo.subPageType}-${landingPage.pageName}`; // set as the action param for trackEvent
    const label = stripSpecialCharsDashReplace(currentTarget.getAttribute('data-eventlabel')); // set as the label param for trackEvent
    const value = stripSpecialCharsDashReplace(currentTarget.getAttribute('data-eventvalue')); // set as the value param for trackEvent

    e.stopPropagation();
    trackLegacyEvent(action, label, value);
  }

  handleSignupClick = e => {
    e.preventDefault();
    const { signupForRewards } = this.props;
    signupForRewards();
  };

  handleRedeemRewardsPoints = spendPoints => {
    this.props.applyRedeemableRewards(spendPoints);
  };

  handleClearRewardsSuccess = () => {
    this.props.clearRewardsSuccess();
  };

  render() {
    const {
      rewards: {
        rewardsInfo,
        isLoading,
        redemptionIncrements,
        redemptionSubmitting,
        redemptionSuccessInfo
      },
      landingPage: { isLoaded, pageInfo, slotOrder },
      location,
      onRedeemRewardsComponentView,
      onChangeRedemptionAmountEvent
    } = this.props;

    if (isLoading || !rewardsInfo || !isLoaded || !pageInfo) {
      return <PageLoader centered={true}/>;
    }

    const params = parse(location.search);
    const isNewPrimeMember = params['np'] ? true : false;
    const { spendPoints, spendPointsDollarValue, consented, canEnroll, prime } = rewardsInfo;
    const isPrimeMember = !!prime; // to avoid passing null
    const { slotData } = pageInfo;
    const { testId } = this.context;
    const rewardsPrimeData = slotData?.['primary-1'];

    return (
      <SiteAwareMetadata>
        <div className={css.rewardsDashboard}>
          <BackNav to="/account">My Account</BackNav>
          <Breadcrumb items={items}/>
          <AccountHeader
            title="Rewards"
            showCustomerEmail={false}
            showFirstName={true}
            prime={isPrimeMember} />
          { rewardsPrimeData && (<RewardsPrimeBanner prime={isPrimeMember} justLinked={isNewPrimeMember} content={rewardsPrimeData} />) }
          {consented && (
            <div>
              <div className={css.rewards}>
                <div className={css.rewardsProgress}>
                  <RewardsProgress size={285} rewardsInfo={rewardsInfo}/>
                </div>
                <div className={css.pointBreakdown}>
                  <h3>My Point Breakdown</h3>
                  <div className={css.redeemablePoints}>
                    <span>Redeemable Points:</span>
                    <span data-test-id={testId('redeemablePoints')}>{toThousandsSeparator(spendPoints)}</span>
                  </div>
                  <div>
                    <span>Redeemable Dollars:</span>
                    <span data-test-id={testId('redeemableDollars')}>{spendPointsDollarValue > 0 ? toUSD(spendPointsDollarValue) : '$0.00'}</span>
                  </div>
                  {spendPointsDollarValue < 0 && (
                    <p className={css.negativePointsMessage}>You currently have no redeemable points. For more information see the <MartyLink to="/c/rewards-faq">Rewards FAQ here</MartyLink>.</p>
                  )}
                </div>

                <div className={css.redeemPointsOnline}>
                  <RedeemableRewards
                    isRedeemingRewards={redemptionSubmitting}
                    onRedeemRewardsPoints={this.handleRedeemRewardsPoints}
                    redeemableRewards={redemptionIncrements}
                    redeemingRewardsStatus={null}
                    spendPointDollarValue={spendPointsDollarValue}
                    spendPoints={spendPoints}
                    redemptionSuccessInfo={redemptionSuccessInfo}
                    onClearRewardsSuccess={this.handleClearRewardsSuccess}
                    onChangeRedemptionAmount={onChangeRedemptionAmountEvent}
                    onRedeemRewardsComponentView={onRedeemRewardsComponentView}/>
                  <div className={css.faqLink}>
                    <FAQ size={19}/>
                    <MartyLink to="/c/rewards-faq" data-test-id={testId('redeemFaqs')}>Have Questions? Read the FAQ</MartyLink>
                    <MartyLink className={css.termsAndConditions} to="/rewards-terms-of-use">Terms and Conditions</MartyLink>
                  </div>
                </div>

              </div>
            </div>
          )}
          {!consented && (canEnroll
            ? <RewardsOptIn onSignUp={this.handleSignupClick}/>
            : <p className={css.rewardsIneligible} data-test-id={testId('rewardsIneligibleMessage')}>We're sorry, but your account is not eligible for Zappos Rewards.</p>)
          }
        </div>

        {consented && (
          <div className={css.rewardsSlots} data-test-id={testId('rewardsWrapper')}>
            {slotOrder.map(slotName => (
              <LandingSlot
                key={slotName}
                slotName={slotName}
                data={slotData[slotName]}
                onLandingComponentClick={this.onLandingComponentClick}/>
            ))}
          </div>
        )}
      </SiteAwareMetadata>
    );
  }
}

RewardsDashboard.defaultProps = {
  trackEvent,
  trackLegacyEvent
};

RewardsDashboard.contextTypes = {
  testId: PropTypes.func
};

const mapStateToProps = state => ({
  rewards: state.rewards,
  landingPage: state.landingPage
});

export default connect(mapStateToProps, {
  applyRedeemableRewards,
  clearRewardsSuccess,
  fetchRewardsInfo,
  fetchRedeemableRewards,
  fetchLandingPageInfo,
  signupForRewards,
  checkCustomerAuthentication,
  onChangeRedemptionAmountEvent,
  onRedeemRewardsComponentView
})(RewardsDashboard);
